import type { CleverTapLoginProps, CleverTapProfileProps } from './sdk/types';
import { getisAppView } from '@lib/utils';
import { publishCleverTapEvent } from '@lib/pusher';

/** CleverTap's opt-out marker, appended to the device guid server-side. */
const OPTED_OUT_GUID_SUFFIX = ':OO';

/** localStorage key the SDK persists its device guid under. */
const GUID_STORAGE_KEY = 'WZRK_G';

/**
 * Reads the device guid straight from localStorage, for the window before the
 * SDK has attached `getCleverTapID` to `window.clevertap`.
 *
 * The SDK writes it URL-encoded and JSON-quoted (`%22<guid>%3AOO%22`), so the
 * decode mirrors the SDK's own (`JSON.parse(decodeURIComponent(value))`). Both
 * steps are tolerant: a plain, already-decoded guid still resolves rather than
 * throwing, since the storage format is the SDK's business and could change.
 */
const readStoredGuid = (): string | undefined => {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(GUID_STORAGE_KEY);
  } catch {
    // Private mode / restricted storage — treat as unknown.
    return undefined;
  }

  if (!raw) return undefined;

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding; fall through with the raw value.
  }

  try {
    const parsed = JSON.parse(decoded);
    if (typeof parsed === 'string' && parsed.length > 0) return parsed;
  } catch {
    // Not JSON-quoted — the decoded value is already the guid.
  }

  return decoded.length > 0 ? decoded : undefined;
};

/**
 * Whether analytics consent is currently granted (FFH-2334).
 *
 * The SDK is only initialised once consent is granted, so this is belt-and-
 * braces for the load — but it is load-bearing for a mid-session WITHDRAWAL:
 * at that point the SDK is already live, and the only SDK-sanctioned way to
 * silence it (`privacy.push({ optOut: true })`) is precisely what we must never
 * call, because it writes `ct_optout` to the user's profile and drops them out
 * of every segment and campaign. So we gate our own calls instead and reload,
 * after which the SDK is simply never initialised again.
 */
let consentGranted = false;

/**
 * Open or close the gate on every outbound CleverTap call. Owned by
 * `useCleverTapConsentRevocation`, which keeps it in step with CookieYes.
 */
export const setCleverTapConsentGate = (granted: boolean) => {
  consentGranted = granted;
};

/**
 * Whether the CleverTap web SDK has been loaded into this page at all.
 *
 * Distinct from `isReady()`, which reports the SDK's onload handshake. This
 * answers the earlier question "is there a live SDK here?", and it goes true as
 * soon as the dynamic import evaluates — before `init()` has finished, and well
 * before readiness resolves.
 *
 * That gap matters on withdrawal: once `init()` has run the SDK auto-tracks
 * page views internally (`pageChanged()`), which our consent gate cannot reach.
 * Deciding whether to reload on readiness alone would leave a live SDK running
 * for the rest of the session if consent was withdrawn while init was still in
 * flight.
 *
 * Deliberately errs towards `true`: the SDK module assigns `window.clevertap`
 * on evaluation, so this can lead `init()` slightly. That costs nothing,
 * because the caller only acts on it after a genuine granted -> denied edge —
 * and consent having been granted is itself what caused us to start the SDK.
 * A reload one beat early is harmless; a missed one leaves it collecting.
 */
export const isCleverTapPresent = (): boolean => {
  try {
    return typeof (window as any).clevertap !== 'undefined';
  } catch {
    return false;
  }
};

export const CleverTap = {
  event(name: string, props: any = {}) {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
      console.info(`[CleverTap] Tracking event: ${name}`, props);
    }
    try {
      const isAppView = getisAppView();
      if (isAppView) {
        // Use Pusher for mobile app communication instead of window.nativeBridge.
        // NOT gated on `consentGranted`: CookieYes never runs in app view, so the
        // gate would be permanently shut and this rail would go dark. The native
        // app owns its own consent.
        publishCleverTapEvent(name, props);
      } else {
        if (!consentGranted) return;
        if ((window as any).clevertap?.event?.push) {
          (window as any).clevertap.event.push(name, props);
        }
      }
    } catch (error) {
      console.error(`[CleverTap] Error tracking event '${name}':`, error);
    }
  },
  /** Returns whether `onUserLogin.push` actually fired. */
  login(props: CleverTapLoginProps): boolean {
    try {
      const isAppView = getisAppView();
      const canPush =
        !isAppView &&
        consentGranted &&
        !!(window as any).clevertap?.onUserLogin?.push;
      // Don't log `props` — it carries PII (Email/Name); log only booleans.
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
        console.info('[CleverTap] login() called', {
          isAppView,
          hasOnUserLoginPush: !!(window as any).clevertap?.onUserLogin?.push,
          willPush: canPush
        });
      }
      if (canPush) {
        (window as any).clevertap.onUserLogin.push(props);
        if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
          console.info('[CleverTap] onUserLogin.push fired');
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error(`[CleverTap] Error tracking login:`, error);
      return false;
    }
  },
  profile: {
    /**
     * Write properties to the CURRENT CleverTap profile (anonymous or
     * identified) via `profile.push`, WITHOUT triggering identity resolution
     * (only `onUserLogin` does that). Used to attach the funnel's Email /
     * MSG-email and result properties to the anonymous `WZRK_G` profile so they
     * merge into the identified profile at login/signup. Web-only; no-op in app
     * view (the native app owns its own profile).
     *
     * Deliberately logs only the property *keys*, never the values — props can
     * carry PII (Email), mirroring the caution in `login()`.
     */
    push(props: CleverTapProfileProps) {
      try {
        if (getisAppView() || !consentGranted) return;
        if ((window as any).clevertap?.profile?.push) {
          (window as any).clevertap.profile.push({ Site: props });
          if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
            console.info('[CleverTap] profile.push', {
              keys: Object.keys(props)
            });
          }
        }
      } catch (error) {
        console.error('[CleverTap] Error pushing profile:', error);
      }
    },
    updateEmailPreference(enableEmail: boolean) {
      try {
        const isAppView = getisAppView();
        if (
          !isAppView &&
          consentGranted &&
          (window as any).clevertap?.profile?.push
        ) {
          (window as any).clevertap.profile.push({
            Site: {
              'MSG-email': enableEmail
            }
          });
          if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
            console.info(`[CleverTap] Email preference updated:`, {
              'MSG-email': enableEmail
            });
          }
        }
      } catch (error) {
        console.error(`[CleverTap] Error updating email preference:`, error);
      }
    }
  },
  isReady() {
    try {
      const isReady = (window as any).$WZRK_WR.is_onloadcalled?.() === true;
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
        console.info('[CleverTap] isReady check:', isReady);
      }
      return isReady;
    } catch (error) {
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
        console.error('[CleverTap] Error checking ready state:', error);
      }
      return false;
    }
  },
  /**
   * Whether the CleverTap DEVICE is currently opted out of data collection
   * (FFH-2102).
   *
   * CleverTap marks an opted-out device by suffixing its guid (`WZRK_G`) with
   * `:OO`, and drops every outgoing request whose guid carries that suffix
   * before it is sent — silently.
   *
   * Since FFH-2334 the app never opts anyone out, so a `true` here means the
   * device was opted out by the old code (or another client) and its profile is
   * still carrying `ct_optout` server-side: excluded from every segment
   * calculation and from all campaigns. That makes this a health probe for the
   * damage already done, and the trigger for any future self-heal — NOT
   * something to answer with a `privacy.push({ optOut: ... })` of any value.
   *
   * `getCleverTapID()` returns `device.getGuid()`, the same in-memory value the
   * SDK's own opt-out check reads, so it keeps the suffix. The localStorage
   * fallback covers the window before the SDK has attached its API, and mirrors
   * the SDK's own parse (`JSON.parse(decodeURIComponent(value))`) while also
   * tolerating an already-decoded value.
   *
   * Returns `undefined` when the guid cannot be determined — callers must treat
   * that as "not yet known" rather than "opted in", or they will conclude
   * consent succeeded before the SDK has said anything.
   */
  isOptedOut(): boolean | undefined {
    try {
      // The native app owns its own consent; there is no web device to inspect.
      if (getisAppView()) return undefined;

      const fromSdk = (window as any).clevertap?.getCleverTapID?.();
      const guid =
        typeof fromSdk === 'string' && fromSdk.length > 0
          ? fromSdk
          : readStoredGuid();

      if (!guid) return undefined;
      return guid.endsWith(OPTED_OUT_GUID_SUFFIX);
    } catch (error) {
      console.error('[CleverTap] Error checking opt-out state:', error);
      return undefined;
    }
  }
};

export { ClevertapEventProperties, ClevertapEvents } from './sdk';
